import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { DialogProvider } from './DialogContext';
import Login from './Login';
import ChangePassword from './ChangePassword';
import Navigation from './Navigation';
import Home from './Home';
import FormsPage from './FormsPage';
import TemplatesPage from './TemplatesPage';
import TemplateBuilder from './TemplateBuilder';
import TeamPage from './TeamPage';
import BillingPage from './BillingPage';
import FormBuilder from './FormBuilder';
import FormFiller from './FormFiller';
import OneTimeSurvey from './OneTimeSurvey';
import FormSubmissionsPage from './FormSubmissionsPage';
import SubmissionView from './SubmissionView';
import { isOneTime } from './roles';
import './App.css';

function AppContent() {
  const { user, logout } = useAuth();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  if (!user) {
    return <Login />;
  }

  // Accounts on a one-time password must pick their own before entering the
  // app (first login and after a dev reset).
  if (user.must_change_password) {
    return <ChangePassword forced />;
  }

  // A one-time account has exactly one screen — the survey it was created for.
  // It gets its own shell rather than the usual one with every link hidden,
  // because there is no dashboard, forms list or team behind them to hide.
  if (isOneTime(user.role)) {
    return <OneTimeSurvey />;
  }

  if (changePasswordOpen) {
    return <ChangePassword onClose={() => setChangePasswordOpen(false)} />;
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Navigation
        user={user}
        onLogout={logout}
        onChangePassword={() => setChangePasswordOpen(true)}
      />
      <main className="min-h-screen">
        <Routes>
          <Route path="/home" element={<Home />} />
          <Route path="/forms" element={<FormsPage />} />
          <Route path="/forms/:id/edit" element={<FormBuilder />} />
          <Route path="/forms/:id/fill" element={<FormFiller />} />
          <Route path="/forms/:id/submissions" element={<FormSubmissionsPage />} />
          {/* Submissions are reached from a form now, but a single one keeps
              its own URL so the dashboard and bookmarks can deep-link to it. */}
          <Route path="/submitted/:id" element={<SubmissionView />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/templates/:id/edit" element={<TemplateBuilder />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/companies/:companyId" element={<BillingPage />} />
          <Route path="/" element={<Home />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <DialogProvider>
          <AppContent />
        </DialogProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
